import { useState, useCallback, useRef, useEffect } from 'react'

const MAX_HISTORY_SIZE = 50

/**
 * Custom hook for managing timeline editor state
 * Handles edited lyrics, undo/redo history, zoom level, and selections
 */
const DEFAULT_RESET_KEY = '__default__'

const useTimelineState = (initialLyrics = [], resetKey = DEFAULT_RESET_KEY) => {
  // Core state - ensure initialLyrics is always an array
  const [lyrics, setLyrics] = useState(Array.isArray(initialLyrics) ? initialLyrics : [])
  const [selectedBlockIndex, setSelectedBlockIndex] = useState(null)
  const [zoom, setZoom] = useState(100) // 25-400%
  const [snapToGrid, setSnapToGrid] = useState(true)
  const [gridSize, setGridSize] = useState(100) // milliseconds

  // Undo/redo state
  const [history, setHistory] = useState([])
  const historyIndexRef = useRef(-1)  // Use ref to avoid stale closures
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Track original lyrics to detect changes - ensure it's always an array
  const originalLyricsRef = useRef(Array.isArray(initialLyrics) ? initialLyrics : [])

  const resetKeyRef = useRef()

  // Update lyrics when a new reset key is provided (e.g., new song loaded)
  useEffect(() => {
    const isNewKey = resetKeyRef.current !== resetKey
    const hasLyricsMeaningfullyChanged = JSON.stringify(initialLyrics) !== JSON.stringify(originalLyricsRef.current)

    // Reset EVERYTHING if it's a new key
    if (isNewKey) {
      resetKeyRef.current = resetKey
      const safeLyrics = Array.isArray(initialLyrics) ? initialLyrics : []
      setLyrics(safeLyrics)
      originalLyricsRef.current = safeLyrics
      setHistory([])
      historyIndexRef.current = -1
      setHasUnsavedChanges(false)
      setSelectedBlockIndex(null)
      return
    }

    // If key is the same but initialLyrics changed (e.g. loaded later),
    // and we haven't made any edits yet, update the lyrics.
    if (hasLyricsMeaningfullyChanged && !hasUnsavedChanges) {
      const safeLyrics = Array.isArray(initialLyrics) ? initialLyrics : []
      setLyrics(safeLyrics)
      originalLyricsRef.current = safeLyrics
      setHistory([])
      historyIndexRef.current = -1
    }
  }, [initialLyrics, resetKey, hasUnsavedChanges])

  // Check if there are unsaved changes
  useEffect(() => {
    const changed = JSON.stringify(lyrics) !== JSON.stringify(originalLyricsRef.current)
    setHasUnsavedChanges(changed)
  }, [lyrics])

  // Add current state to history (for undo/redo)
  const addToHistory = useCallback((newLyrics) => {
    setHistory((prevHistory) => {
      // Remove any "future" states if we're not at the end
      const currentIndex = historyIndexRef.current
      const truncatedHistory = prevHistory.slice(0, currentIndex + 1)

      // Add new state
      const newHistory = [...truncatedHistory, newLyrics]

      // Keep only last MAX_HISTORY_SIZE entries
      if (newHistory.length > MAX_HISTORY_SIZE) {
        return newHistory.slice(newHistory.length - MAX_HISTORY_SIZE)
      }

      return newHistory
    })

    historyIndexRef.current = Math.min(historyIndexRef.current + 1, MAX_HISTORY_SIZE - 1)
  }, [])

  // Update lyrics and add to history
  const updateLyrics = useCallback((newLyrics) => {
    addToHistory(lyrics) // Save current state before updating
    setLyrics(newLyrics)
  }, [lyrics, addToHistory])

  // Update a single lyric at index
  const updateLyric = useCallback((index, updatedLyric) => {
    const newLyrics = [...lyrics]
    newLyrics[index] = { ...newLyrics[index], ...updatedLyric }
    updateLyrics(newLyrics)
  }, [lyrics, updateLyrics])

  // Add a new lyric
  const addLyric = useCallback((newLyric) => {
    const newLyrics = [...lyrics, newLyric]
    // Sort by start_time
    newLyrics.sort((a, b) => a.start_time - b.start_time)
    updateLyrics(newLyrics)
  }, [lyrics, updateLyrics])

  // Remove a lyric at index
  const removeLyric = useCallback((index) => {
    const newLyrics = lyrics.filter((_, i) => i !== index)
    updateLyrics(newLyrics)
    if (selectedBlockIndex === index) {
      setSelectedBlockIndex(null)
    } else if (selectedBlockIndex > index) {
      setSelectedBlockIndex(selectedBlockIndex - 1)
    }
  }, [lyrics, selectedBlockIndex, updateLyrics])

  // Undo operation - using ref to avoid stale closures
  const undo = useCallback(() => {
    const currentIndex = historyIndexRef.current
    if (currentIndex >= 0 && Array.isArray(history[currentIndex])) {
      setLyrics(history[currentIndex])
      historyIndexRef.current = currentIndex - 1
    }
  }, [history])

  // Redo operation - using ref to avoid stale closures
  const redo = useCallback(() => {
    const currentIndex = historyIndexRef.current
    if (currentIndex < history.length - 1) {
      const nextIndex = currentIndex + 1
      if (Array.isArray(history[nextIndex])) {
        setLyrics(history[nextIndex])
        historyIndexRef.current = nextIndex
      }
    }
  }, [history])

  // Check if undo/redo are available - using ref
  const canUndo = historyIndexRef.current >= 0
  const canRedo = historyIndexRef.current < history.length - 1

  // Snap time to grid
  const snapTime = useCallback((time) => {
    if (!snapToGrid) return time
    const gridSizeSeconds = gridSize / 1000
    return Math.round(time / gridSizeSeconds) * gridSizeSeconds
  }, [snapToGrid, gridSize])

  // Reset to original lyrics
  const resetLyrics = useCallback(() => {
    setLyrics(originalLyricsRef.current)
    setHistory([])
    historyIndexRef.current = -1
    setHasUnsavedChanges(false)
    setSelectedBlockIndex(null)
  }, [])

  const replaceLyrics = useCallback((newLyrics, { resetHistory = false } = {}) => {
    const safeLyrics = Array.isArray(newLyrics)
      ? newLyrics.map(lyric => ({ ...lyric }))
      : []

    setLyrics(safeLyrics)

    const maxIndex = safeLyrics.length > 0 ? safeLyrics.length - 1 : null
    if (selectedBlockIndex !== null) {
      if (maxIndex === null) {
        setSelectedBlockIndex(null)
      } else if (selectedBlockIndex > maxIndex) {
        setSelectedBlockIndex(maxIndex)
      }
    }

    if (resetHistory) {
      setHistory([])
      historyIndexRef.current = -1
    }
  }, [selectedBlockIndex])

  // Mark changes as saved
  const markAsSaved = useCallback(() => {
    originalLyricsRef.current = lyrics
    setHasUnsavedChanges(false)
    setHistory([])
    historyIndexRef.current = -1
  }, [lyrics])

  // Validate lyrics (check for overlaps, invalid times)
  const validateLyrics = useCallback((lyricsToValidate = lyrics) => {
    const errors = []

    for (let i = 0; i < lyricsToValidate.length; i++) {
      const lyric = lyricsToValidate[i]

      // Check basic time validity
      if (lyric.start_time < 0) {
        errors.push({ index: i, message: 'Start time cannot be negative' })
      }

      if (lyric.end_time <= lyric.start_time) {
        errors.push({ index: i, message: 'End time must be greater than start time' })
      }

      // Check for overlaps with next lyric
      if (i < lyricsToValidate.length - 1) {
        const nextLyric = lyricsToValidate[i + 1]
        if (lyric.end_time > nextLyric.start_time) {
          errors.push({
            index: i,
            message: `Overlaps with next lyric (ends at ${lyric.end_time.toFixed(2)}s, next starts at ${nextLyric.start_time.toFixed(2)}s)`
          })
        }
      }

      // Check for empty text
      if (!lyric.text || lyric.text.trim() === '') {
        errors.push({ index: i, message: 'Text cannot be empty' })
      }
    }

    return errors
  }, [lyrics])

  return {
    // State
    lyrics,
    selectedBlockIndex,
    zoom,
    snapToGrid,
    gridSize,
    hasUnsavedChanges,
    canUndo,
    canRedo,

    // Actions
    setLyrics: updateLyrics,
    updateLyric,
    addLyric,
    removeLyric,
    setSelectedBlockIndex,
    setZoom,
    setSnapToGrid,
    setGridSize,
    undo,
    redo,
    snapTime,
    resetLyrics,
    replaceLyrics,
    markAsSaved,
    validateLyrics,
  }
}

export default useTimelineState
